import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { ArrowLeft, Printer, Package, MapPin, Phone, Mail, Calendar, Hash } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PackingSlipPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAdminAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchOrder();
  }, [orderId, isAuthenticated]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4540A]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  const shipping = order.shipping || {};
  const customerName = order.customer_name || `${shipping.first_name || ''} ${shipping.last_name || ''}`.trim();
  const items = order.items || [];
  const totals = order.totals || {};
  const createdDate = order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: letter;
            margin: 0.5in;
          }
        }
      `}</style>

      {/* Navigation bar - hidden when printing */}
      <div className="no-print bg-[#1a1a1a] text-white p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link 
            to={`/admin/orders/${orderId}`}
            className="flex items-center gap-2 text-sm hover:text-[#D4540A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Order
          </Link>
          <Button 
            onClick={handlePrint}
            className="bg-[#D4540A] hover:bg-[#B84709] text-white flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Print Packing Slip
          </Button>
        </div>
      </div>

      {/* Packing Slip - Print Area */}
      <div className="print-area bg-white min-h-screen" ref={printRef}>
        <div className="max-w-4xl mx-auto p-8">
          {/* Header */}
          <div className="flex justify-between items-start border-b-2 border-black pb-6 mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-wider">COUNTRY BO</h1>
              <p className="text-gray-600 mt-1">Premium Seasoning Blends</p>
            </div>
            <div className="text-right">
              <h2 className="text-xl font-bold uppercase tracking-wide">Packing Slip</h2>
              <div className="mt-2 text-sm text-gray-600">
                <div className="flex items-center justify-end gap-2">
                  <Hash className="w-4 h-4" />
                  <span className="font-mono font-bold text-black">{order.order_id}</span>
                </div>
                <div className="flex items-center justify-end gap-2 mt-1">
                  <Calendar className="w-4 h-4" />
                  <span>{createdDate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Ship To / Bill To */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h3 className="font-bold text-sm uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Ship To
              </h3>
              <div className="text-base">
                <p className="font-bold text-lg">{customerName}</p>
                <p>{shipping.address_line1}</p>
                {shipping.address_line2 && <p>{shipping.address_line2}</p>}
                <p>{shipping.city}, {shipping.state} {shipping.zip_code}</p>
                <p>{shipping.country || 'United States'}</p>
              </div>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h3 className="font-bold text-sm uppercase tracking-wide text-gray-500 mb-3">
                Contact Information
              </h3>
              <div className="space-y-2 text-base">
                {(order.email || shipping.email) && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{order.email || shipping.email}</span>
                  </div>
                )}
                {(order.phone || shipping.phone) && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{order.phone || shipping.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-8">
            <h3 className="font-bold text-sm uppercase tracking-wide text-gray-500 mb-4 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Items to Pack ({items.reduce((sum, item) => sum + (item.qty || item.quantity || 1), 0)} total)
            </h3>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-3 font-bold">Item</th>
                  <th className="text-left py-3 font-bold">SKU</th>
                  <th className="text-center py-3 font-bold w-24">Qty</th>
                  <th className="text-center py-3 font-bold w-24">Packed</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index} className="border-b border-gray-200">
                    <td className="py-4">
                      <span className="font-medium text-lg">{item.name}</span>
                    </td>
                    <td className="py-4">
                      <span className="font-mono text-sm text-gray-600">{item.sku || item.product_id}</span>
                    </td>
                    <td className="py-4 text-center">
                      <span className="text-2xl font-bold">{item.qty || item.quantity || 1}</span>
                    </td>
                    <td className="py-4 text-center">
                      <div className="w-8 h-8 border-2 border-gray-400 mx-auto rounded"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Order Summary */}
          <div className="flex justify-end mb-8">
            <div className="w-64 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between py-1 text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span>${(totals.subtotal || order.subtotal || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1 text-sm">
                <span className="text-gray-600">Shipping</span>
                <span>${(totals.shipping || order.shipping_cost || 5.99).toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 border-t border-gray-300 mt-2 font-bold">
                <span>Total</span>
                <span>${(totals.total || order.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          {order.notes && (
            <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-bold text-sm uppercase tracking-wide text-yellow-700 mb-2">Order Notes</h3>
              <p className="text-yellow-800">{order.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-black pt-6 mt-8">
            <div className="flex justify-between items-center text-sm text-gray-500">
              <div>
                <p className="font-bold text-black">Country Bo Foods</p>
                <p>support@countrybofoods.com</p>
              </div>
              <div className="text-right">
                <p>Thank you for your order!</p>
                <p className="font-bold text-black mt-1">Put Some Bo On It.</p>
              </div>
            </div>
          </div>

          {/* Warehouse Use Section */}
          <div className="mt-8 p-4 border-2 border-dashed border-gray-300 rounded-lg">
            <h3 className="font-bold text-sm uppercase tracking-wide text-gray-500 mb-3">Warehouse Use Only</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Packed By:</p>
                <div className="border-b border-gray-300 h-6 mt-1"></div>
              </div>
              <div>
                <p className="text-gray-500">Date:</p>
                <div className="border-b border-gray-300 h-6 mt-1"></div>
              </div>
              <div>
                <p className="text-gray-500">QC Check:</p>
                <div className="border-b border-gray-300 h-6 mt-1"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PackingSlipPage;
